import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { getPublicAgents } from '../services/api';
import { Search, Filter, Shield, Wifi, WifiOff, ChevronDown, ExternalLink } from 'lucide-react';

const CAPABILITIES = [
  'fetch_deals', 'price_watch', 'negotiate', 'research',
  'buy_products', 'enrich_data', 'inventory_scout',
  'seo_audit', 'review_orders', 'fulfill_task',
];

const TIERS = [
  { value: '', label: 'All Tiers' },
  { value: 'newcomer', label: 'Newcomer', color: 'text-gray-400' },
  { value: 'active', label: 'Active', color: 'text-amber-600' },
  { value: 'established', label: 'Established', color: 'text-gray-300' },
  { value: 'professional', label: 'Professional', color: 'text-yellow-400' },
  { value: 'elite', label: 'Elite', color: 'text-cyan-300' },
  { value: 'legendary', label: 'Legendary', color: 'text-purple-300' },
];

const BADGE_STYLES = {
  gray: 'bg-gray-500/15 text-gray-400 border-gray-500/25',
  bronze: 'bg-amber-900/15 text-amber-500 border-amber-700/25',
  silver: 'bg-gray-300/15 text-gray-300 border-gray-400/25',
  gold: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/25',
  platinum: 'bg-cyan-500/15 text-cyan-300 border-cyan-400/25',
  diamond: 'bg-purple-500/15 text-purple-300 border-purple-400/25',
};

const SORT_OPTIONS = [
  { value: 'score', label: 'Highest Score' },
  { value: 'earnings', label: 'Top Earners' },
  { value: 'activity', label: 'Most Active' },
  { value: 'newest', label: 'Newest' },
];

function AgentCard({ agent }) {
  const badgeStyle = BADGE_STYLES[agent.badge] || BADGE_STYLES.gray;

  return (
    <Link
      to={`/agents/${agent.agent_id}`}
      data-testid={`agent-card-${agent.agent_id}`}
      className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 hover:bg-[hsl(var(--card))]/80 transition-all cursor-pointer hover:border-emerald-500/30 group p-5 block"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400 font-mono text-sm font-bold">
            {agent.name.charAt(0)}
          </div>
          <div>
            <h3 className="font-semibold text-[hsl(var(--foreground))] text-sm group-hover:text-emerald-400 transition-colors flex items-center gap-1.5">
              {agent.name}
              {agent.wallet_verified && <Shield className="w-3.5 h-3.5 text-emerald-400" />}
            </h3>
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold mt-0.5 ${badgeStyle}`}>
              {agent.tier}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold font-mono text-emerald-400">{agent.ci_agent_score}</div>
          <div className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]">
            {agent.online ? (
              <><Wifi className="w-3 h-3 text-emerald-400" /><span className="text-emerald-400">Online</span></>
            ) : (
              <><WifiOff className="w-3 h-3" /><span>Offline</span></>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3 line-clamp-2 leading-relaxed">{agent.description}</p>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {agent.capabilities?.slice(0, 4).map(cap => (
          <span key={cap} className="text-xs px-2 py-0.5 rounded bg-[hsl(var(--secondary))]/50 text-[hsl(var(--muted-foreground))] font-mono">
            {cap}
          </span>
        ))}
        {agent.capabilities?.length > 4 && (
          <span className="text-xs px-2 py-0.5 rounded bg-[hsl(var(--secondary))]/50 text-[hsl(var(--muted-foreground))]">
            +{agent.capabilities.length - 4}
          </span>
        )}
      </div>

      <div className="flex justify-between text-xs text-[hsl(var(--muted-foreground))] font-mono border-t border-[hsl(var(--border))]/50 pt-2.5">
        <span>{agent.tasks_completed || 0} tasks</span>
        <span>${(agent.total_earned_usdc || 0).toLocaleString()} earned</span>
        <span className="text-[hsl(var(--muted-foreground))]/70">{agent.protocol}</span>
      </div>
    </Link>
  );
}

export default function DiscoverPage() {
  const [agents, setAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCap, setSelectedCap] = useState('');
  const [selectedTier, setSelectedTier] = useState('');
  const [sortBy, setSortBy] = useState('score');
  const [showFilters, setShowFilters] = useState(false);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (selectedCap) params.set('capability', selectedCap);
      if (selectedTier) params.set('tier', selectedTier);
      params.set('sort', sortBy);
      params.set('limit', '24');
      const data = await getPublicAgents(params.toString());
      setAgents(data.agents || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch agents:', err);
    } finally {
      setLoading(false);
    }
  }, [search, selectedCap, selectedTier, sortBy]);

  useEffect(() => {
    const timer = setTimeout(fetchAgents, 300);
    return () => clearTimeout(timer);
  }, [fetchAgents]);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 data-testid="discover-title" className="text-3xl font-bold tracking-tight mb-2">Discover Agents</h1>
          <p className="text-[hsl(var(--muted-foreground))]">
            <span className="font-mono text-emerald-400">{total}</span> agents registered on the Commerce Index network
          </p>
        </div>

        {/* Search + Filter Bar */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
            <input
              data-testid="discover-search"
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search agents by name..."
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 text-[hsl(var(--foreground))] text-sm placeholder:text-[hsl(var(--muted-foreground))]/50 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20"
            />
          </div>

          <button
            data-testid="discover-filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 text-sm hover:bg-[hsl(var(--card))] transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
          </button>

          <select
            data-testid="discover-sort"
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="px-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/50 text-sm text-[hsl(var(--foreground))] focus:outline-none focus:border-emerald-500/50"
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div data-testid="discover-filters-panel" className="mb-6 p-5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 space-y-4">
            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-2 block">Capability</label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCap('')}
                  className={`px-3 py-1 rounded-full text-xs transition-colors ${
                    !selectedCap ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-[hsl(var(--secondary))]/30 text-[hsl(var(--muted-foreground))] border border-transparent hover:border-[hsl(var(--border))]'
                  }`}
                >
                  All
                </button>
                {CAPABILITIES.map(cap => (
                  <button
                    key={cap}
                    data-testid={`filter-cap-${cap}`}
                    onClick={() => setSelectedCap(cap === selectedCap ? '' : cap)}
                    className={`px-3 py-1 rounded-full text-xs font-mono transition-colors ${
                      selectedCap === cap ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-[hsl(var(--secondary))]/30 text-[hsl(var(--muted-foreground))] border border-transparent hover:border-[hsl(var(--border))]'
                    }`}
                  >
                    {cap.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-mono uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-2 block">Tier</label>
              <div className="flex flex-wrap gap-2">
                {TIERS.map(t => (
                  <button
                    key={t.value}
                    data-testid={`filter-tier-${t.value || 'all'}`}
                    onClick={() => setSelectedTier(t.value)}
                    className={`px-3 py-1 rounded-full text-xs transition-colors ${
                      selectedTier === t.value ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-[hsl(var(--secondary))]/30 text-[hsl(var(--muted-foreground))] border border-transparent hover:border-[hsl(var(--border))]'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Agent Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))]/30 p-5 h-48 animate-pulse" />
            ))}
          </div>
        ) : agents.length === 0 ? (
          <div data-testid="discover-empty" className="text-center py-20 text-[hsl(var(--muted-foreground))]">
            <Search className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg mb-2">No agents found</p>
            <p className="text-sm">Try adjusting your filters or search term</p>
          </div>
        ) : (
          <div data-testid="discover-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agents.map(agent => (
              <AgentCard key={agent.agent_id} agent={agent} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
